/**
 * @file ResetePasswordForm.tsx
 * @description
 * -----------------------------------------------------
 * Archivo que contiene el componente de formulario de reseteo de contraseña
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-01-15
 * @modified 2025-10-06
 * @author DLTCode
 * @author Mohammed Derfoufi
 * @author Ismael Benabdellah
 * @author Nacho Moya
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { c as _c } from "react/compiler-runtime";
import { startTransition, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import InputComponent from "@/components/ui/InputComponent";
import ButtonComponent from "@/components/ui/ButtonComponent";
import { toast } from "sonner";
import { passwordReset } from "../actions";
export type ResetPasswordFormProps = {
  token?: string;
  onSuccess?: () => void;
};
const ResetPasswordForm = t0 => {
  const $ = _c(50);
  const {
    token: tokenFromProps,
    onSuccess
  } = t0;
  const searchParams = useSearchParams();
  const label = useTranslations("Labels");
  const placeholder = useTranslations("Placeholders");
  const buttons = useTranslations("Buttons");
  const modals = useTranslations("Modals");
  let t1;
  if ($[0] !== searchParams || $[1] !== tokenFromProps) {
    t1 = tokenFromProps || searchParams?.get("token") || "";
    $[0] = searchParams;
    $[1] = tokenFromProps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const token = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      password: "",
      repeatPassword: ""
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [formData, setFormData] = useState(t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const [errors, setErrors] = useState(t3);
  const [isPending, setIsPending] = useState(false);
  let t4;
  if ($[5] !== errors || $[6] !== formData) {
    t4 = e => {
      const {
        id,
        value
      } = e.target;
      setFormData({
        ...formData,
        [id]: value
      });
      if (errors[id]) {
        setErrors(prevErrors => {
          const newErrors = {
            ...prevErrors
          };
          delete newErrors[id];
          return newErrors;
        });
      }
    };
    $[5] = errors;
    $[6] = formData;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleChange = t4;
  let t5;
  if ($[8] !== onSuccess) {
    t5 = e_0 => {
      e_0.preventDefault();
      const data = new FormData(e_0.target as HTMLFormElement);
      setIsPending(true);
      startTransition(async () => {
        const response = await passwordReset(data);
        if (response && "fieldErrors" in response) {
          setErrors(response.fieldErrors);
          setIsPending(false);
          return;
        }
        if (response && response.error) {
          toast.error(response.message || response.error);
          setIsPending(false);
          return;
        }
        setIsPending(false);
        onSuccess?.();
      });
    };
    $[8] = onSuccess;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleSubmit = t5;
  let t6;
  if ($[10] !== token) {
    t6 = <input type="hidden" name="token" value={token} readOnly={true} />;
    $[10] = token;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== label) {
    t7 = label("labelNewPassword");
    $[12] = label;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== placeholder) {
    t8 = placeholder("placeholderNewPassword");
    $[14] = placeholder;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = errors.password && errors.password[0];
  let t10;
  if ($[16] !== formData.password || $[17] !== handleChange || $[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t10 = <InputComponent id="password" name="password" label={t7} type="password" placeholder={t8} className="input--full" value={formData.password} onChange={handleChange} error={t9} autoComplete="new-password" isRequired={true} />;
    $[16] = formData.password;
    $[17] = handleChange;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== label) {
    t11 = label("labelConfirmNewPassword");
    $[22] = label;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== placeholder) {
    t12 = placeholder("placeholderConfirmNewPassword");
    $[24] = placeholder;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = errors.repeatPassword && errors.repeatPassword[0];
  let t14;
  if ($[26] !== formData.repeatPassword || $[27] !== handleChange || $[28] !== t11 || $[29] !== t12 || $[30] !== t13) {
    t14 = <InputComponent id="repeatPassword" name="repeatPassword" label={t11} type="password" placeholder={t12} className="input--full" value={formData.repeatPassword} onChange={handleChange} error={t13} autoComplete="new-password" isRequired={true} />;
    $[26] = formData.repeatPassword;
    $[27] = handleChange;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== modals) {
    t15 = modals("requiredFieldsMessage");
    $[32] = modals;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15) {
    t16 = <p className="auth__required-message">{t15}</p>;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== buttons) {
    t17 = buttons("buttonAccept");
    $[36] = buttons;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== isPending || $[39] !== t17) {
    t18 = <ButtonComponent type="submit" disabled={isPending} loading={isPending}>{t17}</ButtonComponent>;
    $[38] = isPending;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t16 || $[42] !== t18) {
    t19 = <section className="reset-password__footer">{t16}{t18}</section>;
    $[41] = t16;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== handleSubmit || $[45] !== t10 || $[46] !== t14 || $[47] !== t19 || $[48] !== t6) {
    t20 = <form className="auth__form-base auth__reset-password-form" onSubmit={handleSubmit}>{t6}{t10}{t14}{t19}</form>;
    $[44] = handleSubmit;
    $[45] = t10;
    $[46] = t14;
    $[47] = t19;
    $[48] = t6;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  return t20;
};
export default ResetPasswordForm;